/**
 * Подъём Telegram SDK, который НЕ имеет права сорвать монтирование React (work-039).
 *
 * Было: `initSDK()` вызывался прямо в теле `main.tsx`, до `createRoot(...).render(...)`.
 * Вне Telegram он бросает `LaunchParamsRetrieveError`, исключение на уровне модуля
 * обрывало выполнение — и React не монтировался вообще. `<ErrorBoundary>` при этом
 * бессилен по устройству: он ловит ошибки рендера УЖЕ смонтированного дерева, а тут
 * дерева не существует. Итог — пустой `#root` и белый экран
 * (воспроизведено на проде 2026-07-21: `document.getElementById('root').innerHTML === ''`).
 *
 * Стало: все три шага подъёма — здесь, каждый в своём try/catch. Модуль возвращает
 * дискретный статус, а решение «что рендерить» принимает `main.tsx`.
 *
 * Два класса отказов разведены намеренно:
 *  - `initSDK()` упал → 'unavailable'. Launch params недоступны, значит нет ни
 *    пропуска для API, ни языка, ни темы: App нежизнеспособен, показываем
 *    `NotInTelegramScreen`.
 *  - `bindThemeCssVars()` / `initViewport()` упали → это косметика (цвета и
 *    safe-area, у обоих есть CSS-фолбэки). Ронять из-за них рабочее приложение
 *    нельзя, поэтому отказ репортим и идём дальше.
 *
 * CANON G3: функция никогда не бросает. Но и НЕ глушит молча — каждый отказ
 * уезжает в ловушку mini-watch (см. `reportToWatch`).
 */
import { bindThemeCssVars, initSDK, initViewport } from '../lib/tg.js';

/**
 * 'ready' — SDK поднят, можно рендерить `App`.
 * 'unavailable' — launch params недоступны (запуск вне Telegram).
 */
export type SdkBootResult = 'ready' | 'unavailable';

/**
 * Ре-репорт отказа в глобальное событие `window 'error'` — так он доезжает до
 * ловушки mini-watch, которая слушает именно window-события (`boot/armWatch.ts`,
 * вооружён вторым импортом в `main.tsx`, то есть уже до нашего вызова).
 * Образцы того же приёма — `components/ErrorBoundary.tsx:52` и
 * `lib/sessionExpiry.ts` (`reportToWatch`).
 *
 * Исходный `Error` передаётся как есть, чтобы не потерять stack; контекст
 * («какой шаг упал») кладём в `message` события.
 */
function reportToWatch(stage: string, cause: unknown): void {
  try {
    const err = cause instanceof Error ? cause : new Error(String(cause));
    window.dispatchEvent(
      new ErrorEvent('error', {
        error: err,
        message: `sdk-boot failed at ${stage}: ${err.message}`,
      }),
    );
  } catch {
    // dispatchEvent/ErrorEvent недоступны в окружении — наблюдаемость не
    // критична, экран человеку всё равно будет показан.
  }
}

/**
 * Поднять SDK: `initSDK()` → `bindThemeCssVars()` → `initViewport()`.
 *
 * Порядок обязателен: обе последние функции требуют инициализированного SDK
 * (см. комментарии в `lib/tg.ts`).
 *
 * Cleanup от `initSDK()` намеренно не сохраняется и не вызывается: в Mini App
 * нет момента unmount окна, SDK живёт всю жизнь страницы.
 */
export function bootSdk(): SdkBootResult {
  try {
    initSDK();
  } catch (cause) {
    reportToWatch('init', cause);
    // Дальше идти нет смысла: тема и viewport без SDK всё равно недоступны.
    return 'unavailable';
  }

  try {
    bindThemeCssVars();
  } catch (cause) {
    reportToWatch('theme-css-vars', cause);
  }

  try {
    initViewport();
  } catch (cause) {
    reportToWatch('viewport', cause);
  }

  return 'ready';
}
